<template>

    <div class="flex flex-wrap items-end gap-4 mb-5">
        <div class="flex flex-col">
            <label for="orders_date_from" class="text-sm font-medium text-gray-700">From</label>
            <input id="orders_date_from" type="date" v-model="dateFrom" class="mt-1 block rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
        </div>

        <div class="flex flex-col">
            <label for="orders_date_to" class="text-sm font-medium text-gray-700">To</label>
            <input id="orders_date_to" type="date" v-model="dateTo" class="mt-1 block rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
        </div>

        <button v-if="dateFrom || dateTo" type="button" @click="clearDateFilter" class="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
            Clear
        </button>

        <button
            type="button"
            @click="exportDisplayedOrdersCsv"
            :disabled="!displayedOrders.length"
            class="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
            Export CSV
        </button>
    </div>

    <div class="flex items-center space-x-2 mb-5" v-if="selectedLeads.length">
        <div class="relative inline-block text-left">
            <div>
                <button @click="showDropdown = !showDropdown" type="button" class="inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-1 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-100"  aria-expanded="true" aria-haspopup="true">
                    Mark Orders
                    <svg class="-mr-1 ml-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
                    </svg>
                </button>
            </div>

            <div v-if="showDropdown" class="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none" role="menu" aria-orientation="vertical" aria-labelledby="menu-button" tabindex="-1">
                <div class="py-1" role="none">
                    <span @click="markOrder('completed')" class="text-green-500 block px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer" role="menuitem" tabindex="-1">Completed</span>
                    <span @click="markOrder('cancelled')" class="text-red-500 block px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer border-b border-gray-300" role="menuitem" tabindex="-1">Cancelled</span>
                    <span @click="markOrder('processing')" class="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer" role="menuitem" tabindex="-1">Processing</span>
                    <span @click="markOrder('created')" class="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer" role="menuitem" tabindex="-1">Created</span>
                </div>
            </div>
        </div>
    </div>

    <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
        <table class="min-w-full divide-y divide-gray-300">
            <thead class="bg-gray-50">
            <tr>
                <th scope="col" class="relative px-7 sm:w-12 sm:px-6">
                    <input type="checkbox" v-model="selectAll" :value="!selectAll" :checked="selectAll" class="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600">
                </th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Installer</th>
                <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Customer</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Door Style</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Amount</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Payment Status</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Date</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Details</th>
            </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 bg-white">
            <tr :class="{'bg-gray-50': isLeadSelected(order.id)}" v-for="(order, index) in displayedOrders">
                <td class="relative px-7 sm:w-12 sm:px-6">
                    <!-- Selected row marker, only show when row is selected. -->

                    <div v-if="isLeadSelected(order.id)" class="absolute inset-y-0 left-0 w-0.5 bg-indigo-600"></div>
                    <input type="checkbox" v-model="selectedLeads" :value="order.id" :checked="isLeadSelected(order.id)" class="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600">
                </td>
                <td class="whitespace-nowrap pl-4 pr-3 py-4 text-sm font-medium text-gray-900 sm:pl-6" :class="{'text-indigo-600': isLeadSelected(order.id)}">{{ order.installer ? order.installer.business_name : 'N/A' }}</td>
                <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6" :class="{'text-indigo-600': isLeadSelected(order.id)}">
                    <!-- <a :href="'/leads/'+order.lead.reference"> -->
                    {{ order.customer ? order.customer.name : 'N/A' }}
                    <!-- </a> -->
                </td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ (order.order_items && order.order_items.length) ? order.order_items[0].uppercase_doorstyle : 'N/A' }}</td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ order.formatted_amount }}</td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-left">
                    <span
                        class="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold"
                        :style="getPaymentStatusClasses(order.payment_status).style"
                    >
                        <svg class="h-2 w-2" :style="getPaymentStatusClasses(order.payment_status).dotStyle" viewBox="0 0 8 8"><circle cx="4" cy="4" r="3" /></svg>
                        {{ order.payment_status ? order.payment_status.replaceAll('_', ' ') : 'unpaid' }}
                    </span>
                </td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-left">
                    <span
                        class="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold"
                        :style="getStatusClasses(order.status).style"
                    >
                        <svg class="h-2 w-2" :style="getStatusClasses(order.status).dotStyle" viewBox="0 0 8 8"><circle cx="4" cy="4" r="3" /></svg>
                        {{ order.status ? order.status.replaceAll('_', ' ') : '' }}
                    </span>
                </td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ order.date }}</td>
                <td class="whitespace-nowrap px-3 py-4 text-xs text-gray-500 hover:text-blue-500">
                    <a :href="'/orders/'+order.id"><i class="fa-solid fa-eye text-lg"></i></a>
                    <!-- <ul v-if="lead.assigned_to.length">
                        <li v-for="(user, index) in lead.assigned_to">{{ user }}</li>
                    </ul>
                    <span v-else>-</span> -->
                </td></tr>
            </tbody>
        </table>
    </div>
</template>
<script>
export default {
    props: ['orders'],
    data() {
        return {
            selectedLeads: [],
            selectAll: false,
            orders_data: this.orders,
            dateFrom: '',
            dateTo: '',
            showDropdown: false,
            statusClasses: {
                completed: { style: { backgroundColor: '#dcfce7', color: '#15803d', boxShadow: 'inset 0 0 0 1px rgba(22, 163, 74, 0.2)' }, dotStyle: { fill: '#22c55e' } },
                cancelled: { style: { backgroundColor: '#fee2e2', color: '#b91c1c', boxShadow: 'inset 0 0 0 1px rgba(220, 38, 38, 0.1)' }, dotStyle: { fill: '#ef4444' } },
                processing: { style: { backgroundColor: '#dbeafe', color: '#1d4ed8', boxShadow: 'inset 0 0 0 1px rgba(29, 78, 216, 0.1)' }, dotStyle: { fill: '#3b82f6' } },
                created: { style: { backgroundColor: '#dbeafe', color: '#1d4ed8', boxShadow: 'inset 0 0 0 1px rgba(29, 78, 216, 0.1)' }, dotStyle: { fill: '#3b82f6' } },
            },
            paymentStatusClasses: {
                paid: { style: { backgroundColor: '#dcfce7', color: '#15803d', boxShadow: 'inset 0 0 0 1px rgba(22, 163, 74, 0.2)' }, dotStyle: { fill: '#22c55e' } },
                unpaid: { style: { backgroundColor: '#fef9c3', color: '#854d0e', boxShadow: 'inset 0 0 0 1px rgba(202, 138, 4, 0.2)' }, dotStyle: { fill: '#eab308' } },
            },
        }
    },
    watch: {
        selectAll() {
            if (this.selectAll) {
                this.selectedLeads = this.displayedOrders.map(order => order.id);
            } else {
                this.selectedLeads = [];
            }
        },
        dateFrom() {
            this.pruneSelectionsToDisplayedOrders();
        },
        dateTo() {
            this.pruneSelectionsToDisplayedOrders();
        }
    },
    computed: {
        displayedOrders() {
            const { fromDate, toDate } = this.getNormalizedDateRange();

            if (!fromDate && !toDate) return this.orders_data;

            return this.orders_data.filter(order => {
                const orderDate = this.getOrderCreatedAtDate(order);
                if (!orderDate) return false;

                if (fromDate && orderDate < fromDate) return false;
                if (toDate && orderDate > toDate) return false;

                return true;
            })
        }
    },
    methods: {
        clearDateFilter() {
            this.dateFrom = '';
            this.dateTo = '';
        },
        exportDisplayedOrdersCsv() {
            const orders = Array.isArray(this.displayedOrders) ? this.displayedOrders : [];
            if (!orders.length) return;

            const baseHeaders = [
                'order_id',
                'installer_business_name',
                'customer_name',
                'customer_email',
                'customer_phone',
                'full_address',
                'amount',
                'formatted_amount',
                'payment_status',
                'payment_method',
                'stripe_payment_intent',
                'status',
                'date',
                'created_at',
                'updated_at',
            ];

            const maxItems = orders.reduce((max, order) => {
                const count = Array.isArray(order?.order_items) ? order.order_items.length : 0;
                return Math.max(max, count);
            }, 0);

            const itemKeySet = new Set();
            orders.forEach(order => {
                const items = Array.isArray(order?.order_items) ? order.order_items : [];
                items.forEach(item => {
                    if (!item || typeof item !== 'object') return;
                    Object.keys(item).forEach(key => itemKeySet.add(key));
                });
            });

            const itemKeys = Array.from(itemKeySet).sort((a, b) => String(a).localeCompare(String(b)));

            const itemHeaders = [];
            for (let index = 0; index < maxItems; index += 1) {
                for (const key of itemKeys) {
                    itemHeaders.push(`item${index + 1}_${key}`);
                }
            }

            const headers = [...baseHeaders, 'items_count', ...itemHeaders];
            const rows = orders.map(order => this.buildOrderCsvRow(order, itemKeys, maxItems));

            const csvContent = this.toCsvString(headers, rows);
            const filename = this.buildOrdersCsvFilename();
            this.downloadCsv(csvContent, filename);
        },
        buildOrderCsvRow(order, itemKeys, maxItems) {
            const items = Array.isArray(order?.order_items) ? order.order_items : [];
            const row = {
                order_id: order?.id ?? '',
                installer_business_name: order?.installer?.business_name ?? '',
                customer_name: order?.customer?.name ?? '',
                customer_email: order?.customer?.email ?? '',
                customer_phone: order?.customer?.phone ?? '',
                full_address: order?.full_address ?? '',
                amount: order?.amount ?? '',
                formatted_amount: order?.formatted_amount ?? '',
                payment_status: order?.payment_status ?? '',
                payment_method: order?.payment_method ?? '',
                stripe_payment_intent: order?.stripe_payment_intent ?? '',
                status: order?.status ?? '',
                date: order?.date ?? '',
                created_at: order?.created_at ?? '',
                updated_at: order?.updated_at ?? '',
                items_count: items.length,
            };

            for (let index = 0; index < maxItems; index += 1) {
                const item = items[index];
                for (const key of itemKeys) {
                    row[`item${index + 1}_${key}`] = item ? item[key] : '';
                }
            }

            return row;
        },
        toCsvString(headers, rows) {
            const escape = value => {
                if (value === null || value === undefined) return '""';
                let rawValue = value;
                if (Array.isArray(rawValue) || (typeof rawValue === 'object' && rawValue !== null)) {
                    try {
                        rawValue = JSON.stringify(rawValue);
                    } catch (e) {
                        rawValue = String(rawValue);
                    }
                }
                const text = String(rawValue);
                return `"${text.replace(/"/g, '""')}"`;
            };

            const headerLine = headers.map(h => escape(h)).join(',');
            const dataLines = rows.map(row => headers.map(header => escape(row?.[header] ?? '')).join(','));
            return [headerLine, ...dataLines].join('\r\n');
        },
        buildOrdersCsvFilename() {
            const datePart = new Date().toISOString().slice(0, 10);
            const from = this.dateFrom ? String(this.dateFrom) : 'all';
            const to = this.dateTo ? String(this.dateTo) : 'all';
            return `orders_${datePart}_from_${from}_to_${to}.csv`;
        },
        downloadCsv(content, filename) {
            const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            URL.revokeObjectURL(url);
        },
        pruneSelectionsToDisplayedOrders() {
            this.selectAll = false;
            const displayedIds = new Set(this.displayedOrders.map(order => order.id));
            this.selectedLeads = this.selectedLeads.filter(id => displayedIds.has(id));
        },
        getOrderCreatedAtDate(order) {
            if (order && order.created_at) {
                const date = new Date(order.created_at);
                if (!Number.isNaN(date.getTime())) return date;
            }

            if (order && order.date) {
                const match = String(order.date).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
                if (match) {
                    const day = Number(match[1]);
                    const month = Number(match[2]) - 1;
                    const year = Number(match[3]);
                    const date = new Date(year, month, day);
                    if (!Number.isNaN(date.getTime())) return date;
                }
            }

            return null;
        },
        getNormalizedDateRange() {
            const fromDate = this.dateFrom ? this.getLocalStartOfDay(this.dateFrom) : null;
            const toDate = this.dateTo ? this.getLocalEndOfDay(this.dateTo) : null;

            if (fromDate && toDate && fromDate > toDate) {
                return { fromDate: toDate, toDate: fromDate };
            }

            return { fromDate, toDate };
        },
        getLocalStartOfDay(dateString) {
            const parts = String(dateString).split('-').map(Number);
            if (parts.length !== 3 || parts.some(n => Number.isNaN(n))) return null;
            return new Date(parts[0], parts[1] - 1, parts[2], 0, 0, 0, 0);
        },
        getLocalEndOfDay(dateString) {
            const start = this.getLocalStartOfDay(dateString);
            if (!start) return null;
            return new Date(start.getFullYear(), start.getMonth(), start.getDate(), 23, 59, 59, 999);
        },
        fetch() {
            axios.get('/leads/fetch').then(response => {
                if (response.data.leads) {
                    this.orders_data = response.data.leads;
                }
            })
        },
        submit() {
            axios.patch('/leads', {
                bulk_close: true,
                selected_leads: this.selectedLeads,
            }).then(response => {
                this.fetch();
                this.selectedLeads = [];
            }).catch(error => {
                //
            })
        },
        markOrder(status) {
            axios.patch('/configurator-orders', {
                bulk_close: true,
                status: status,
                close_order: 'true',
                selected_leads: this.selectedLeads,
            }).then(response => {
                window.location.reload();
            })
        },
        isLeadSelected(id) {
            return this.selectedLeads.includes(id)
        },
        getStatusClasses(status) {
            const normalizedStatus = status ? status.toLowerCase().trim() : '';
            return this.statusClasses[normalizedStatus] || this.statusClasses[status] || { style: { backgroundColor: '#f3f4f6', color: '#4b5563', boxShadow: 'inset 0 0 0 1px rgba(107, 114, 128, 0.1)' }, dotStyle: { fill: '#9ca3af' } };
        },
        getPaymentStatusClasses(status) {
            const normalizedStatus = status ? status.toLowerCase().trim() : 'unpaid';
            return this.paymentStatusClasses[normalizedStatus] || this.paymentStatusClasses['unpaid'];
        }
    }
}
</script>
